import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { CartLine, Product } from "@neus/shared";
import { track } from "../api/track";

export interface CartItem {
  sku: string;
  qty: number;
  /** Display snapshot only; the server reprices every checkout. */
  name: string;
  brand: string;
  price: number;
  image?: string;
}

interface CartState {
  items: CartItem[];
  add: (product: Product, qty: number) => void;
  setQty: (sku: string, qty: number) => void;
  remove: (sku: string) => void;
  clear: () => void;
}

export const MAX_QTY = 99;

export const useCart = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      add: (product, qty) => {
        // Count only the units the cart accepts (it caps each line at MAX_QTY).
        const before = get().items.find((i) => i.sku === product.sku)?.qty ?? 0;
        const added = Math.min(MAX_QTY, before + qty) - before;
        if (added > 0) track({ type: "add_to_cart", sku: product.sku, qty: added });
        set((s) => {
          const existing = s.items.find((i) => i.sku === product.sku);
          const snapshot = {
            name: product.name,
            brand: product.brand,
            price: product.price,
            image: product.images[0]?.url,
          };
          if (existing) {
            return {
              items: s.items.map((i) =>
                i.sku === product.sku ? { ...i, ...snapshot, qty: Math.min(MAX_QTY, i.qty + qty) } : i,
              ),
            };
          }
          return { items: [...s.items, { sku: product.sku, qty: Math.min(MAX_QTY, qty), ...snapshot }] };
        });
      },
      setQty: (sku, qty) =>
        set((s) => ({
          items:
            qty <= 0
              ? s.items.filter((i) => i.sku !== sku)
              : s.items.map((i) => (i.sku === sku ? { ...i, qty: Math.min(MAX_QTY, qty) } : i)),
        })),
      remove: (sku) => set((s) => ({ items: s.items.filter((i) => i.sku !== sku) })),
      clear: () => set({ items: [] }),
    }),
    { name: "neus-cart", version: 1, storage: createJSONStorage(() => localStorage) },
  ),
);

export const selectCount = (s: CartState) => s.items.reduce((n, i) => n + i.qty, 0);
export const selectSubtotal = (s: CartState) => s.items.reduce((n, i) => n + i.qty * i.price, 0);
export const toCartLines = (items: CartItem[]): CartLine[] => items.map(({ sku, qty }) => ({ sku, qty }));
